import { takeLatest, call, put, all, fork } from 'redux-saga/effects';
import { signUp, validateUser, signIn } from '@services/authenticationService';
import { cognitoSignUp } from '@services/cognitoUserService';
import { Types, onSignUpSuccess, onSignUpFail } from './actions';
import { openPopUp } from '../../PopUp/actions';
import { TYPES } from '../../PopUp/popups/SignUpUnavailable';
import POPUP_TYPES from '../../PopUp/types';
import { signInSuccess } from '../SignIn/actions';
import { getConversationsAction } from '@containers/App/containers/Messaging/actions';
import { changeLocale } from '@containers/LanguageProvider/actions';
import { setRedirectUrl } from '@containers/App/components/Redirector/actions';
import { ROUTES } from '@shared/constants';

const USERNAME_ALREADY_EXISTS = 'User already exists';
const BACK_BUTTON_TEXT = 'Back';

const popUpSagaHelper = (list) => {
  if (list[0]) return TYPES.COUNTRY;
  if (list[1]) return TYPES.STATE;
  if (list[2]) return TYPES.AGE;
};

function* callPostSignUpData(action) {
  try {
    const { firstName, lastName, email, birthdate, password, country, state, available, locale, cognitoSub: cognitoSubFromPayload, completeSignup, inviteToken } = action.payload;
    // 1. Validate user before Cognito and DB signup
    const validationResponse = yield call(validateUser, {
      firstName,
      lastName,
      email,
      birthdate,
      country,
      state,
      available,
      locale,
      inviteToken,
    });
    if (!validationResponse.valid) {
      if (validationResponse.message === USERNAME_ALREADY_EXISTS) {
        yield put(
          openPopUp(POPUP_TYPES.ALERT, {
            titleText: validationResponse.message || 'Email already exists',
            buttonText: BACK_BUTTON_TEXT,
          }),
        );
      }
      if (validationResponse.isLead) {
        yield put(
          openPopUp(POPUP_TYPES.SIGN_UP_UNAVAILABLE, {
            type: popUpSagaHelper([
              validationResponse.country,
              validationResponse.state,
              validationResponse.underAge,
            ]),
            hideCloseIcon: true,
          }),
        );
      }
      yield put(onSignUpFail(validationResponse.message || 'Validation failed'));
      return;
    }
    // 2. Register user with Cognito (skipped for complete-signup flow)
    let cognitoSub = cognitoSubFromPayload;
    let codeDeliveryDetails = null;
    if (!completeSignup) {
      const cognitoResult = yield call(cognitoSignUp, { email, password });
      cognitoSub = cognitoResult?.userSub || cognitoResult?.user?.sub;
      codeDeliveryDetails = cognitoResult?.codeDeliveryDetails || cognitoResult?.CodeDeliveryDetails || null;
    }

    if (!cognitoSub) {
      throw new Error('Missing Cognito Sub');
    }

    // 3. Register user profile in backend (without password)
    const signUpPayload = {
      firstName,
      lastName,
      birthdate,
      country,
      state,
      locale,
      email,
      cognitoSub,
      inviteToken,
      ...(completeSignup ? { completeSignup: true } : {}),
    };

    yield call(signUp, signUpPayload);

    if (completeSignup) {
      // Fetch user details and enter the app.
      const user = yield call(signIn, cognitoSub);
      if (user) {
        yield put(signInSuccess(user));
        yield put(getConversationsAction());
        if (user.locale) {
          yield put(changeLocale(user.locale.toLowerCase()));
        }
      }
      // Stop local "sending" state without triggering the legacy signup redirects.
      yield put({ type: Types.SIGN_UP_SUCCESS, available: false, completeSignup: true });
      yield put(setRedirectUrl(ROUTES.HOME));
      return;
    }

    yield put(onSignUpSuccess(available, codeDeliveryDetails));
  } catch (error) {
    if (error.message === USERNAME_ALREADY_EXISTS) {
      yield put(
        openPopUp(POPUP_TYPES.ALERT, {
          titleText: error.message || 'Email already exists',
          buttonText: BACK_BUTTON_TEXT,
        }),
      );
    }
    yield put(onSignUpFail(error.message));
  }
}

function* watchCallPostSignUpData() {
  yield takeLatest(Types.SIGN_UP, callPostSignUpData);
}

export default function* watchLoginSignActions() {
  yield all([fork(watchCallPostSignUpData)]);
}
